import React, { memo } from 'react';
import { createPortal } from 'react-dom';
import { useModalMaestroEntidad } from './Hooks/useModalMaestroEntidad';
import { onlyNumbers } from '../helpers';

const CONDICIONES_IVA = [
    'IVA Responsable Inscripto',
    'Responsable Monotributo',
    'IVA Exento',
    'Consumidor Final',
    'Cliente del Exterior'
];

const TIPOS_CUENTA = [
    'Cuenta Corriente ARS',
    'Caja de Ahorro ARS',
    'Caja de Ahorro USD',
    'Cuenta Corriente USD',
    'Cuenta Virtual',
];

const ModalMaestroEntidad = ({ show, onClose, onSuccess, tipoEntidad, initialData = null }) => {
    const {
        formData, setFormData,
        subconceptos,
        nuevoSubconceptoInput, setNuevoSubconceptoInput,
        loading, error, config,
        handleAddSubconcepto, handleRemoveSubconcepto,
        handleSubmit
    } = useModalMaestroEntidad({ show, onClose, onSuccess, tipoEntidad, initialData });

    if (!show) return null;

    return createPortal(
        <div className="fixed inset-0 bg-gray-900 bg-opacity-60 z-[1100] flex items-center justify-center p-4 backdrop-blur-sm transition-opacity text-left font-sans">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-auto max-h-[92vh] flex flex-col overflow-hidden border border-gray-100 animate-fade-in transition-all duration-300">
                
                {/* HEADER DEL MODAL */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100 bg-blue-50/50 shrink-0">
                    <div className="flex items-center gap-3">
                        <span className="bg-blue-100 text-blue-600 p-2.5 rounded-xl shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </span>
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 leading-none">
                                {initialData ? 'Editar' : 'Nuevo'} {config.title}
                            </h3>
                            <p className="text-[10px] text-blue-800/80 font-black mt-1.5 tracking-wider uppercase select-none">
                                {config.subtitle}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-slate-50 rounded-full cursor-pointer">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* FORMULARIO DE EDICIÓN */}
                <div className="overflow-y-auto p-8 flex-grow bg-white scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
                    <form id="maestroForm" onSubmit={handleSubmit} className="space-y-6">
                        
                        {error && (
                            <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold">
                                ⚠️ {error}
                            </div>
                        )}

                        {/* CLIENTES Y PROVEEDORES */}
                        {(tipoEntidad === 'empresa_clientes' || tipoEntidad === 'empresa_proveedores') && (
                            <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                                <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3 left-4 tracking-tight">Datos Comerciales & Fiscales</legend>
                                
                                <div className="space-y-4 pt-2">
                                    <div>
                                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Razón Social *</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.razonSocial}
                                            onChange={e => setFormData(prev => ({ ...prev, razonSocial: e.target.value }))}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all uppercase"
                                            placeholder="Ej: ACOMPAÑAR S.R.L."
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">CUIT (Solo números)</label>
                                            <input
                                                type="text"
                                                value={formData.cuit}
                                                onChange={e => setFormData(prev => ({ ...prev, cuit: onlyNumbers(e.target.value) }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                                placeholder="30712345678"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Condición IVA</label>
                                            <select
                                                value={formData.condicionIva}
                                                onChange={e => setFormData(prev => ({ ...prev, condicionIva: e.target.value }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all cursor-pointer"
                                            >
                                                <option value="">Seleccionar...</option>
                                                {CONDICIONES_IVA.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Email de Contacto</label>
                                            <input
                                                type="email"
                                                value={formData.email}
                                                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-medium text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                                placeholder="contacto@empresa.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Teléfono (Solo números)</label>
                                            <input
                                                type="text"
                                                value={formData.telefono}
                                                onChange={e => setFormData(prev => ({ ...prev, telefono: onlyNumbers(e.target.value) }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                                placeholder="1112345678"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* CUENTAS BANCARIAS Y VIRTUALES */}
                        {tipoEntidad === 'empresa_cuentas' && (
                            <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm space-y-4">
                                <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3 left-4 tracking-tight">Datos de Cuenta & Tesorería</legend>
                                
                                <div className="space-y-4 pt-2">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Banco / Entidad *</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.banco}
                                                onChange={e => setFormData(prev => ({ ...prev, banco: e.target.value }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all uppercase"
                                                placeholder="EJ. SUPERVIELLE"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Titular / Razón Social *</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.titular}
                                                onChange={e => setFormData(prev => ({ ...prev, titular: e.target.value }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all uppercase"
                                                placeholder="EJ. IB BROKERS S.A."
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tipo de Cuenta *</label>
                                            <select
                                                required
                                                value={formData.tipoCuenta}
                                                onChange={e => setFormData(prev => ({ ...prev, tipoCuenta: e.target.value }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all cursor-pointer"
                                            >
                                                <option value="">Seleccionar Tipo...</option>
                                                {TIPOS_CUENTA.map(t => <option key={t} value={t}>{t}</option>)}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">CUIT del Titular</label>
                                            <input
                                                type="text"
                                                value={formData.cuitTitular}
                                                onChange={e => setFormData(prev => ({ ...prev, cuitTitular: onlyNumbers(e.target.value) }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                                placeholder="30712345678"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">N° de Cuenta (Interno)</label>
                                            <input
                                                type="text"
                                                value={formData.numeroCuenta}
                                                onChange={e => setFormData(prev => ({ ...prev, numeroCuenta: e.target.value }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                                placeholder="Ej. CC-12345/6"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">CBU / CVU (22 dígitos)</label>
                                            <input
                                                type="text"
                                                maxLength={22}
                                                value={formData.cbu}
                                                onChange={e => setFormData(prev => ({ ...prev, cbu: onlyNumbers(e.target.value).slice(0, 22) }))}
                                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                                placeholder="0270000011123456789012"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* ÁREAS */}
                        {tipoEntidad === 'empresa_areas' && (
                            <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                                <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3 left-4 tracking-tight">Centro de Costo</legend>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                    <div>
                                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Nombre del Área *</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.nombre}
                                            onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all uppercase"
                                            placeholder="Ej: ADMINISTRACIÓN"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">CUIT Asociado</label>
                                        <input
                                            type="text"
                                            value={formData.cuit}
                                            onChange={e => setFormData(prev => ({ ...prev, cuit: onlyNumbers(e.target.value) }))}
                                            className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                            placeholder="30712345678"
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        )}

                        {/* CONCEPTOS DE FACTURACIÓN */}
                        {tipoEntidad === 'empresa_conceptos_facturas' && (
                            <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm">
                                <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3 left-4 tracking-tight">Concepto Venta</legend>
                                
                                <div className="pt-2">
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Nombre del Concepto de Facturación *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.nombre}
                                        onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all uppercase"
                                        placeholder="Ej: COMISIONES MARCOS TROVATO"
                                    />
                                </div>
                            </fieldset>
                        )}

                        {/* CONCEPTOS DE GASTOS */}
                        {tipoEntidad === 'empresa_conceptos_gastos' && (
                            <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm space-y-5">
                                <legend className="px-3 text-base font-bold text-slate-700 bg-white absolute -top-3 left-4 tracking-tight">Estructura de Gastos</legend>
                                
                                <div className="pt-2">
                                    <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Concepto Padre de Gasto *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.nombre}
                                        onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all uppercase"
                                        placeholder="Ej: SERVICIOS / INFRAESTRUCTURA"
                                    />
                                </div>

                                <div className="border-t border-slate-100 pt-4 space-y-3">
                                    <label className="block text-[12px] font-black text-slate-600 uppercase tracking-widest pl-1">Subconceptos Asignados</label>
                                    
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={nuevoSubconceptoInput}
                                            onChange={e => setNuevoSubconceptoInput(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubconcepto(); } }}
                                            className="flex-grow border border-slate-200 rounded-xl px-4 py-2 text-sm font-bold text-slate-800 outline-none bg-white h-[42px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all uppercase"
                                            placeholder="Ej: LUZ / TELEFONÍA / CLOUD"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddSubconcepto}
                                            className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-bold uppercase text-xs shadow-md transition-all shrink-0 cursor-pointer"
                                        >
                                            + Asignar
                                        </button>
                                    </div>

                                    <div className="flex flex-wrap gap-2 pt-2">
                                        {subconceptos.length === 0 ? (
                                            <span className="text-xs text-slate-400 italic">No hay subconceptos vinculados a este rubro de gasto.</span>
                                        ) : (
                                            subconceptos.map((sub, index) => (
                                                <span key={index} className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-900 font-bold px-3 py-1.5 rounded-xl text-xs uppercase shadow-sm">
                                                    {sub}
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSubconcepto(index)}
                                                        className="text-indigo-400 hover:text-rose-600 font-black text-sm ml-1 transition-colors cursor-pointer"
                                                        title="Quitar Subconcepto"
                                                    >
                                                        ✕
                                                    </button>
                                                </span>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                        )}

                    </form>
                </div>

                {/* BOTONERA INFERIOR */}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 shrink-0">
                    <button type="button" onClick={onClose} className="px-6 py-2.5 rounded-full border border-slate-300 bg-white text-slate-700 font-bold uppercase text-xs hover:bg-slate-50 shadow-sm transition-all transform active:scale-95 select-none cursor-pointer">
                        Cancelar
                    </button>
                    <button type="submit" form="maestroForm" disabled={loading} className="px-8 py-2.5 rounded-full text-white font-bold transition-all uppercase text-xs transform active:scale-95 min-w-[180px] shadow-lg select-none bg-blue-600 hover:bg-blue-700 shadow-blue-100 cursor-pointer">
                        {loading ? 'Guardando...' : 'Confirmar cambios'}
                    </button>
                </div>

            </div>
        </div>,
        document.body
    );
};

export default memo(ModalMaestroEntidad);